import type { PublicAccount } from '@postpilot/core/domain';
import { toast } from 'sonner';
import { create } from 'zustand';
import { api, ApiError } from '@/lib/api';

interface AccountsState {
  accounts: PublicAccount[];
  loading: boolean;
  error: string | null;

  /**
   * Which accounts the composer will post to.
   *
   * Lives here rather than in the composer's local state so it survives
   * navigation — a user who selects 4 accounts, wanders to the queue to check
   * something, then comes back should not have to select them all again.
   */
  selectedIds: string[];

  fetch: () => Promise<void>;
  disconnect: (id: string) => Promise<void>;
  connect: (returnTo?: string) => Promise<void>;

  toggleSelected: (id: string) => void;
  setSelected: (ids: string[]) => void;
  selectAll: () => void;
  clearSelected: () => void;
}

export const useAccountsStore = create<AccountsState>((set, get) => ({
  accounts: [],
  loading: false,
  error: null,
  selectedIds: [],

  fetch: async () => {
    set({ loading: true, error: null });
    try {
      const accounts = await api.accounts.list();

      // Prune selections whose account has since been disconnected — otherwise
      // the composer would silently try to post to an account that is gone and
      // get a 403 the user cannot explain.
      const live = new Set(accounts.map((a) => a.id));
      const selectedIds = get().selectedIds.filter((id) => live.has(id));

      set({ accounts, selectedIds, loading: false });
    } catch (error) {
      set({
        error: error instanceof ApiError ? error.message : 'Could not load your accounts.',
        loading: false,
      });
    }
  },

  connect: async (returnTo = '/accounts') => {
    try {
      const url = await api.oauth.start(returnTo);
      // A full-page navigation, not a popup: popups are blocked by default in
      // most browsers unless they open synchronously from a click, and this URL
      // arrives from an await.
      window.location.href = url;
    } catch (error) {
      toast.error('Could not start the connection', {
        description: error instanceof ApiError ? error.message : 'Please try again.',
      });
    }
  },

  disconnect: async (id) => {
    const account = get().accounts.find((a) => a.id === id);

    // Optimistic: remove it now, restore it if the server disagrees. Disconnect
    // is a click the user wants to feel instant.
    const previous = get().accounts;
    set({
      accounts: previous.filter((a) => a.id !== id),
      selectedIds: get().selectedIds.filter((sid) => sid !== id),
    });

    try {
      await api.accounts.disconnect(id);
      toast.success(`Disconnected @${account?.username ?? 'account'}`);
    } catch (error) {
      set({ accounts: previous });
      toast.error('Could not disconnect', {
        description: error instanceof ApiError ? error.message : 'Please try again.',
      });
    }
  },

  toggleSelected: (id) => {
    const selected = get().selectedIds;
    set({
      selectedIds: selected.includes(id) ? selected.filter((sid) => sid !== id) : [...selected, id],
    });
  },

  setSelected: (ids) => set({ selectedIds: ids }),

  // Only active accounts. Selecting a revoked one guarantees a failed post.
  selectAll: () =>
    set({
      selectedIds: get()
        .accounts.filter((a) => a.status === 'active')
        .map((a) => a.id),
    }),

  clearSelected: () => set({ selectedIds: [] }),
}));

/** The accounts the composer is allowed to post to right now. */
export const selectActiveAccounts = (state: AccountsState) =>
  state.accounts.filter((a) => a.status === 'active');

/** Accounts that need the user to reconnect them. Surfaced as a banner. */
export const selectBrokenAccounts = (state: AccountsState) =>
  state.accounts.filter((a) => a.status !== 'active');
